import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, X, Check } from "lucide-react";

/**
 * ReferrerPicker — async type-ahead for picking an affiliate to record as the referrer.
 *
 * Props:
 *   value:     the currently-selected referrer affiliate id (string, or "" for none)
 *   onChange:  called with the new id (or "" to clear)
 *   excludeId: an affiliate id to exclude from results (typically the affiliate being edited)
 */
export function ReferrerPicker({ value, onChange, excludeId }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  // Resolve the current value → readable label (once)
  useEffect(() => {
    if (!value) { setSelectedLabel(""); return; }
    let cancelled = false;
    api.get(`/affiliates/${value}`).then((r) => {
      if (!cancelled) setSelectedLabel(r.data?.name || value);
    }).catch(() => { if (!cancelled) setSelectedLabel(value); });
    return () => { cancelled = true; };
  }, [value]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (!q) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get("/affiliates", { params: { q, limit: 10 } }).then((r) => {
        setResults((r.data || []).filter((a) => a.id !== excludeId));
      }).catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [query, open, excludeId]);

  // Close on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const pick = (a) => {
    onChange(a.id);
    setSelectedLabel(a.name);
    setOpen(false);
    setQuery("");
  };

  const clear = () => {
    onChange("");
    setSelectedLabel("");
    setQuery("");
  };

  const displayValue = useMemo(() => {
    if (open) return query;
    if (selectedLabel) return selectedLabel;
    return "";
  }, [open, query, selectedLabel]);

  return (
    <div className="relative mt-1" ref={boxRef}>
      <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 h-10">
        <Search className="h-3.5 w-3.5 text-muted-foreground" />
        <input
          data-testid="referrer-picker-input"
          value={displayValue}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          placeholder={value ? "" : "Search affiliate by name…"}
          className="flex-1 bg-transparent outline-none text-sm"
        />
        {value && !open && (
          <button
            type="button"
            data-testid="referrer-picker-clear"
            onClick={clear}
            className="h-6 w-6 rounded-full flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-foreground"
            title="Clear referrer"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {open && (query.trim() || results.length > 0) && (
        <div
          data-testid="referrer-picker-results"
          className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto rounded-xl border border-border bg-card shadow-lg py-1"
        >
          {results.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">No matches</div>
          )}
          {results.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => pick(a)}
              data-testid={`referrer-picker-option-${a.id}`}
              className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-center justify-between"
            >
              <span className="truncate">
                <span className="font-medium">{a.name}</span>
                <span className="ml-2 text-[10px] uppercase tracking-widest text-muted-foreground">{a.tier}</span>
              </span>
              {value === a.id && <Check className="h-3.5 w-3.5 text-primary" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
